import * as z from 'zod/mini';
import { DEVICE_ID_PATTERN, HLC_PATTERN, SCHEMA_VERSION } from './constants';
import {
  checkinItemDataSchema,
  checkinLogDataSchema,
  dailyNoteDataSchema,
  importantDateDataSchema,
  projectDataSchema,
  templateDataSchema,
  timeBlockDataSchema,
} from './data-schemas';
import { RecordType } from './enums';
import { settingDataSchema } from './settings';

/**
 * Envelope shared by every persisted record (data dictionary §1):
 * `{ id, type, hlc, deleted, schemaVersion, deviceId, data }`, with `data` null if and only if deleted.
 */

export const hlcSchema = z.string().check(z.regex(HLC_PATTERN, 'Invalid hybrid logical clock value'));
export const deviceIdSchema = z.string().check(z.regex(DEVICE_ID_PATTERN, 'Invalid device id'));

const envelopeFields = {
  id: z.uuid(),
  hlc: hlcSchema,
  deleted: z.boolean(),
  schemaVersion: z.literal(SCHEMA_VERSION),
  deviceId: deviceIdSchema,
};

const DATA_NULL_IFF_DELETED = {
  message: 'data must be null if and only if the record is deleted',
  path: ['data'],
};

function dataMatchesDeleted(envelope: { deleted: boolean; data: unknown }): boolean {
  return envelope.deleted === (envelope.data === null);
}

const fieldsOf = <T extends RecordType>(type: T) => ({ ...envelopeFields, type: z.literal(type) });

export const projectEnvelopeSchema = z
  .object({ ...fieldsOf(RecordType.Project), data: z.nullable(projectDataSchema) })
  .check(z.refine(dataMatchesDeleted, DATA_NULL_IFF_DELETED));
export const timeBlockEnvelopeSchema = z
  .object({ ...fieldsOf(RecordType.TimeBlock), data: z.nullable(timeBlockDataSchema) })
  .check(z.refine(dataMatchesDeleted, DATA_NULL_IFF_DELETED));
export const checkinItemEnvelopeSchema = z
  .object({ ...fieldsOf(RecordType.CheckinItem), data: z.nullable(checkinItemDataSchema) })
  .check(z.refine(dataMatchesDeleted, DATA_NULL_IFF_DELETED));
export const checkinLogEnvelopeSchema = z
  .object({ ...fieldsOf(RecordType.CheckinLog), data: z.nullable(checkinLogDataSchema) })
  .check(z.refine(dataMatchesDeleted, DATA_NULL_IFF_DELETED));
export const dailyNoteEnvelopeSchema = z
  .object({ ...fieldsOf(RecordType.DailyNote), data: z.nullable(dailyNoteDataSchema) })
  .check(z.refine(dataMatchesDeleted, DATA_NULL_IFF_DELETED));
export const templateEnvelopeSchema = z
  .object({ ...fieldsOf(RecordType.Template), data: z.nullable(templateDataSchema) })
  .check(z.refine(dataMatchesDeleted, DATA_NULL_IFF_DELETED));
export const importantDateEnvelopeSchema = z
  .object({ ...fieldsOf(RecordType.ImportantDate), data: z.nullable(importantDateDataSchema) })
  .check(z.refine(dataMatchesDeleted, DATA_NULL_IFF_DELETED));
export const settingEnvelopeSchema = z
  .object({ ...fieldsOf(RecordType.Setting), data: z.nullable(settingDataSchema) })
  .check(z.refine(dataMatchesDeleted, DATA_NULL_IFF_DELETED));

export const envelopeSchema = z.discriminatedUnion('type', [
  projectEnvelopeSchema,
  timeBlockEnvelopeSchema,
  checkinItemEnvelopeSchema,
  checkinLogEnvelopeSchema,
  dailyNoteEnvelopeSchema,
  templateEnvelopeSchema,
  settingEnvelopeSchema,
  importantDateEnvelopeSchema,
]);
